import { FC } from 'react'
import { IconButton, IconButtonProps, Tooltip } from '@mui/material'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import CheckIcon from '@mui/icons-material/Check'

import { useCopyToClipboard } from '$hooks'

export interface CopyButtonProps {
  /** The text written to the clipboard when the button is pressed. */
  value: string
  /** Tooltip shown after a successful copy. Defaults to "Copied!". */
  copiedTooltip?: string
  /** Accessible label / hover title for the button. Defaults to "Copy". */
  ariaLabel?: string
  /** Icon button size. Defaults to "small". */
  size?: IconButtonProps['size']
  /** Forwarded to the underlying IconButton (e.g. edge, sx). */
  iconButtonProps?: Omit<IconButtonProps, 'onClick' | 'aria-label' | 'size'>
}

/**
 * A reusable copy-to-clipboard button: copies {@link CopyButtonProps.value} and
 * gives transient feedback by swapping the copy glyph for a check and showing a
 * "Copied" tooltip for a short window. Built on {@link useCopyToClipboard}.
 */
const CopyButton: FC<CopyButtonProps> = ({
  value,
  copiedTooltip = 'Copied!',
  ariaLabel = 'Copy',
  size = 'small',
  iconButtonProps,
}) => {
  const { copied, copy } = useCopyToClipboard()

  const handleCopy = () => {
    copy(value)
  }

  return (
    <Tooltip
      open={copied}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      title={copiedTooltip}
      arrow
    >
      <IconButton
        onClick={handleCopy}
        aria-label={ariaLabel}
        size={size}
        {...iconButtonProps}
      >
        {copied ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <FileCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  )
}

export default CopyButton
